import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Phone, MessageCircle, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StickyConversionBar = () => {
  const [config, setConfig] = useState(null);
  const [globalPresets, setGlobalPresets] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    fetchConfigs();
  }, []);

  useEffect(() => {
    // Check if we should show (mobile only, not on admin pages)
    const checkVisibility = () => {
      const isMobile = window.innerWidth < 1024;
      const isAdminPage = location.pathname.startsWith('/admin');
      const isLoginPage = location.pathname === '/login' || location.pathname === '/register';
      setIsVisible(isMobile && !isAdminPage && !isLoginPage && config?.enable_sticky_bar);
    };

    checkVisibility();
    window.addEventListener('resize', checkVisibility);
    return () => window.removeEventListener('resize', checkVisibility);
  }, [location.pathname, config]);

  const fetchConfigs = async () => {
    try {
      const [convRes, globalRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/landing/conversion-config`),
        fetch(`${BACKEND_URL}/api/landing/global-presets-config`)
      ]);
      
      if (convRes.ok) {
        const data = await convRes.json();
        setConfig(data);
      }
      if (globalRes.ok) {
        const data = await globalRes.json();
        setGlobalPresets(data);
      }
    } catch (error) {
      console.error('Failed to load conversion config:', error);
    }
  };

  const trackClick = async (ctaKey, label, href) => {
    try {
      await fetch(`${BACKEND_URL}/api/public/track-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page: location.pathname,
          cta_key: ctaKey,
          label,
          href
        })
      });
    } catch (e) {
      // Never block navigation
    }
  };

  if (!isVisible || !config) return null;

  const buttons = config.sticky_buttons || {};
  const contact = globalPresets?.contact || {};
  const whatsappLink = contact.whatsapp_link || 'https://wa.me/26876001234';
  const phoneNumber = contact.phone || '+268 7600 1234';

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] lg:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="sticky-conversion-bar"
    >
      <div className="flex items-stretch h-14">
        {/* WhatsApp */}
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackClick('sticky_whatsapp', buttons.whatsapp_text || 'WhatsApp', whatsappLink)}
          className="flex-1 flex items-center justify-center gap-2 bg-emerald-500 text-white font-medium text-sm hover:bg-emerald-600 active:bg-emerald-700 transition-colors"
        >
          <MessageCircle className="w-5 h-5" />
          <span>{buttons.whatsapp_text || 'WhatsApp'}</span>
        </a>

        {/* Secure Spot - Programs */}
        <a
          href="/programs"
          onClick={() => trackClick('sticky_programs', buttons.enroll_text || 'Secure Your Spot', '/programs')}
          className="flex-1 flex items-center justify-center gap-2 bg-primary text-white font-medium text-sm hover:bg-primary/90 active:bg-primary/80 transition-colors"
        >
          <span>{buttons.enroll_text || 'Secure Your Spot'}</span>
          <ChevronRight className="w-4 h-4" />
        </a>

        {/* Call */}
        <a
          href={`tel:${phoneNumber.replace(/\s/g, '')}`}
          onClick={() => trackClick('sticky_call', buttons.call_text || 'Call', `tel:${phoneNumber}`)}
          className="flex items-center justify-center gap-2 px-4 bg-slate-100 text-slate-700 font-medium text-sm hover:bg-slate-200 active:bg-slate-300 transition-colors"
        >
          <Phone className="w-5 h-5" />
          <span className="hidden sm:inline">{buttons.call_text || 'Call'}</span>
        </a>
      </div>
    </div>
  );
};

export default StickyConversionBar;
